import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../OnboardingLayout'
import { healthScreeningQuestions } from '../../lib/i18nCatalog'
import { scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import type { OnboardingStepProps } from '../../pages/onboarding/stepProps'

const HealthScreeningStep = ({ onNext, onPrevious, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const questions = useMemo(() => healthScreeningQuestions(t), [t])
  const [answers, setAnswers] = useState<Record<string, boolean | null>>(() =>
    Object.fromEntries(questions.map((q) => [q.id, null])),
  )
  const [showExclusion, setShowExclusion] = useState<string | null>(null)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const allAnswered = Object.values(answers).every((value) => value !== null)
  const unansweredIds = questions.filter((q) => answers[q.id] === null).map((q) => q.id)

  function handleAnswer(id: string, value: boolean) {
    setAnswers((prev) => ({ ...prev, [id]: value }))
    if (value) {
      const question = questions.find((q) => q.id === id)
      setShowExclusion(question?.exclusion_message ?? null)
    } else if (!Object.entries({ ...answers, [id]: value }).some(([, v]) => v === true)) {
      setShowExclusion(null)
    }
  }

  function handleContinue() {
    if (!allAnswered) {
      setAttemptedSubmit(true)
      scrollToFirstFieldError(unansweredIds)
      return
    }
    const exclusions = questions.filter((q) => answers[q.id] === true).map((q) => q.id)
    onNext({
      health_screening_passed: exclusions.length === 0,
      health_screening_exclusions: exclusions,
      health_screening_completed_at: new Date().toISOString(),
    })
  }

  return (
    <OnboardingLayout
      step={stepIndex}
      totalSteps={ONBOARDING_STEP_COUNT}
      onPrev={onPrevious}
      onNext={handleContinue}
      nextButtonText={allAnswered ? t('onboarding.form.continue') : t('onboarding.healthScreening.answerAll')}
    >
      <div className="flex flex-col gap-5">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.healthScreening.title')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.healthScreening.intro')}</p>
        </div>

        <div className="space-y-3">
          {questions.map((question) => {
            const unanswered = attemptedSubmit && answers[question.id] === null
            return (
              <div
                key={question.id}
                id={`field-${question.id}`}
                className={`scroll-mt-32 ${ONBOARDING.panel} transition-shadow ${
                  unanswered ? 'ring-2 ring-kilo-danger/60 ring-offset-2 ring-offset-kilo-card' : ''
                }`}
              >
                <p className={`mb-3 text-sm leading-relaxed ${ONBOARDING.reviewValue}`}>
                  {question.question}
                  <span className="ml-1 text-kilo-danger" aria-hidden="true">
                    *
                  </span>
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleAnswer(question.id, false)}
                    className={`flex-1 rounded-xl border py-2 text-sm font-medium transition-all ${
                      answers[question.id] === false
                        ? 'border-kilo-accent bg-kilo-accent text-black'
                        : ONBOARDING.chipInactive
                    }`}
                  >
                    {t('onboarding.healthScreening.no')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAnswer(question.id, true)}
                    className={`flex-1 rounded-xl border py-2 text-sm font-medium transition-all ${
                      answers[question.id] === true
                        ? 'border-amber-500 bg-amber-500 text-black'
                        : ONBOARDING.chipInactive
                    }`}
                  >
                    {t('onboarding.healthScreening.yes')}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {attemptedSubmit && !allAnswered ? (
          <div className={ONBOARDING.error}>{t('onboarding.healthScreening.answerAllPrompt')}</div>
        ) : null}

        {showExclusion ? (
          <div className={ONBOARDING.infoWarning}>
            <p className="mb-1 text-xs uppercase tracking-widest text-amber-600 dark:text-amber-400">{t('onboarding.healthScreening.important')}</p>
            <p className="text-sm leading-relaxed">{showExclusion}</p>
            <p className={`mt-2 text-xs ${ONBOARDING.mutedSoft}`}>{t('onboarding.healthScreening.stillUseKilo')}</p>
          </div>
        ) : null}

        <p className={`text-center text-[10px] leading-relaxed ${ONBOARDING.mutedSoft}`}>{t('onboarding.healthScreening.privacyNote')}</p>
      </div>
    </OnboardingLayout>
  )
}

export default HealthScreeningStep
